import hijriMoment from "moment-hijri";

/**
 * A Hijri-calendar date adapter for `@mui/x-date-pickers` (v5's `dateAdapter` prop),
 * mirroring `@date-io/moment`'s public API (the same interface `AdapterMoment` /
 * `AdapterDayjs` implement) so it's a drop-in swap for `UIDatePicker`.
 *
 * Year/month concepts (navigation grids, labels, "same month" checks, etc.) use
 * `moment-hijri`'s `i`-prefixed Hijri units (`iYear`/`iMonth`/`iDate`). Day/hour/
 * minute/second concepts are calendar-agnostic — a day is the same span of time in
 * either calendar — so those are left as plain moment operations, same as the
 * Gregorian adapter.
 *
 * Two moment-hijri gotchas this works around:
 * 1. Importing `moment-hijri` anywhere flips the *global* default locale of the
 *    shared `moment` singleton to "ar-sa" (a module-load side effect of its own
 *    `moment.updateLocale('ar-sa', ...)` call). Every method below resolves its
 *    locale explicitly (`this.locale`) instead of trusting the ambient default.
 * 2. `moment-hijri`'s built-in `.iDaysInMonth()` formats through `parseInt(date.format('iDD'))`,
 *    which breaks under an Arabic-Indic-digit locale (`parseInt("٣٠")` is `NaN`).
 *    `getDaysInMonth` below reads the raw `.iDate()` getter instead, which is
 *    locale-independent.
 */

const defaultFormats = {
  normalDateWithWeekday: "ddd, iD iMMM",
  normalDate: "iD iMMMM",
  shortDate: "iMMM iD",
  monthAndDate: "iMMMM iD",
  dayOfMonth: "iD",
  year: "iYYYY",
  month: "iMMMM",
  monthShort: "iMMM",
  monthAndYear: "iMMMM iYYYY",
  weekday: "dddd",
  weekdayShort: "ddd",
  minutes: "mm",
  hours12h: "hh",
  hours24h: "HH",
  seconds: "ss",
  fullTime: "LT",
  fullTime12h: "hh:mm A",
  fullTime24h: "HH:mm",
  fullDate: "iD iMMMM iYYYY",
  fullDateWithWeekday: "dddd, iD iMMMM iYYYY",
  fullDateTime: "iD iMMMM iYYYY LT",
  fullDateTime12h: "iD iMMM iYYYY hh:mm A",
  fullDateTime24h: "iD iMMM iYYYY HH:mm",
  keyboardDate: "iYYYY/iMM/iDD",
  keyboardDateTime: "iYYYY/iMM/iDD LT",
  keyboardDateTime12h: "iYYYY/iMM/iDD hh:mm A",
  keyboardDateTime24h: "iYYYY/iMM/iDD HH:mm",
};

class AdapterMomentHijri {
  constructor({ locale, formats, instance } = {}) {
    this.moment = instance || hijriMoment;
    this.locale = locale;
    this.lib = "moment-hijri";
    this.formats = { ...defaultFormats, ...formats };
  }

  /** Returns a fresh instance with `this.locale` applied, immune to the ambient-default bug. */
  withLocale = (date) => (this.locale ? date.clone().locale(this.locale) : date);

  is12HourCycleInCurrentLocale = () =>
    /A|a/.test(
      this.moment.localeData(this.getCurrentLocaleCode()).longDateFormat("LT"),
    );

  getFormatHelperText = (format) => {
    const localFormattingTokens = /(\[[^[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})|./g;
    return (
      format
        .match(localFormattingTokens)
        ?.map((token) => {
          const firstCharacter = token[0];
          if (firstCharacter === "L" || firstCharacter === ";") {
            return this.moment
              .localeData(this.getCurrentLocaleCode())
              .longDateFormat(token);
          }
          return token;
        })
        .join("")
        .replace(/a/gi, "(a|p)m")
        .toLocaleLowerCase() ?? format
    );
  };

  getCurrentLocaleCode = () => this.locale || "en";

  parseISO = (isoString) => this.moment(isoString, true);

  toISO = (value) => value.toISOString();

  parse = (value, format) => {
    if (value === "") return null;
    return this.withLocale(this.moment(value, format, true));
  };

  date = (value) => {
    if (value === null) return null;
    return this.withLocale(this.moment(value));
  };

  toJsDate = (value) => value.toDate();

  isValid = (value) => this.moment(value).isValid();

  isNull = (date) => date === null;

  getDiff = (date, comparing, unit) => {
    if (!this.moment(comparing).isValid()) return 0;
    return date.diff(comparing, unit);
  };

  isAfter = (date, value) => date.isAfter(value);

  isBefore = (date, value) => date.isBefore(value);

  isAfterDay = (date, value) => date.isAfter(value, "day");

  isBeforeDay = (date, value) => date.isBefore(value, "day");

  isBeforeMonth = (date, value) =>
    date.iYear() < value.iYear() ||
    (date.iYear() === value.iYear() && date.iMonth() < value.iMonth());

  isAfterMonth = (date, value) =>
    date.iYear() > value.iYear() ||
    (date.iYear() === value.iYear() && date.iMonth() > value.iMonth());

  isBeforeYear = (date, value) => date.iYear() < value.iYear();

  isAfterYear = (date, value) => date.iYear() > value.iYear();

  startOfDay = (date) => date.clone().startOf("day");

  endOfDay = (date) => date.clone().endOf("day");

  format = (date, formatKey) => this.formatByString(date, this.formats[formatKey]);

  formatByString = (date, formatString) => this.withLocale(date).format(formatString);

  formatNumber = (numberToFormat) => numberToFormat;

  getHours = (date) => date.get("hours");

  addSeconds = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "seconds") : date.clone().add(count, "seconds");

  addMinutes = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "minutes") : date.clone().add(count, "minutes");

  addHours = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "hours") : date.clone().add(count, "hours");

  addDays = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "days") : date.clone().add(count, "days");

  addWeeks = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "weeks") : date.clone().add(count, "weeks");

  addMonths = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "iMonth") : date.clone().add(count, "iMonth");

  addYears = (date, count) =>
    count < 0 ? date.clone().subtract(Math.abs(count), "iYear") : date.clone().add(count, "iYear");

  setHours = (date, count) => date.clone().hours(count);

  getMinutes = (date) => date.get("minutes");

  setMinutes = (date, count) => date.clone().minutes(count);

  getSeconds = (date) => date.get("seconds");

  setSeconds = (date, count) => date.clone().seconds(count);

  getMonth = (date) => date.iMonth();

  setMonth = (date, count) => date.clone().iMonth(count);

  /** Avoids moment-hijri's own `.iDaysInMonth()`, which breaks under an Arabic-Indic-digit
   * locale (it round-trips through `parseInt(date.format('iDD'))`). */
  getDaysInMonth = (date) => date.clone().endOf("iMonth").iDate();

  isSameDay = (date, comparing) => date.isSame(comparing, "day");

  isSameMonth = (date, comparing) =>
    date.iYear() === comparing.iYear() && date.iMonth() === comparing.iMonth();

  isSameYear = (date, comparing) => date.iYear() === comparing.iYear();

  isSameHour = (date, comparing) => date.isSame(comparing, "hour");

  getMeridiemText = (ampm) => {
    if (this.is12HourCycleInCurrentLocale()) {
      return this.moment
        .localeData(this.getCurrentLocaleCode())
        .meridiem(ampm === "am" ? 0 : 13, 0, false);
    }
    return ampm === "am" ? "AM" : "PM";
  };

  startOfYear = (date) => date.clone().startOf("iYear");

  endOfYear = (date) => date.clone().endOf("iYear");

  startOfMonth = (date) => date.clone().startOf("iMonth");

  endOfMonth = (date) => date.clone().endOf("iMonth");

  startOfWeek = (date) => date.clone().startOf("week");

  endOfWeek = (date) => date.clone().endOf("week");

  getNextMonth = (date) => date.clone().add(1, "iMonth");

  getPreviousMonth = (date) => date.clone().subtract(1, "iMonth");

  getMonthArray = (date) => {
    const firstMonth = date.clone().startOf("iYear");
    const monthArray = [firstMonth];
    while (monthArray.length < 12) {
      const prevMonth = monthArray[monthArray.length - 1];
      monthArray.push(this.getNextMonth(prevMonth));
    }
    return monthArray;
  };

  getYear = (date) => date.iYear();

  setYear = (date, year) => date.clone().iYear(year);

  getDate = (date) => date.iDate();

  setDate = (date, day) => date.clone().iDate(day);

  mergeDateAndTime = (date, time) =>
    date.hour(time.hour()).minute(time.minute()).second(time.second());

  /** Resolves via `this.locale` explicitly rather than the library's ambient default
   * locale, which `moment-hijri` silently forces to "ar-sa" on import (see module docblock). */
  getWeekdays = () => {
    const locale = this.getCurrentLocaleCode();
    return Array.from({ length: 7 }).map((_unused, i) =>
      this.moment().locale(locale).weekday(i).format("dd"),
    );
  };

  isEqual = (value, comparing) => {
    if (value === null && comparing === null) return true;
    return this.moment(value).isSame(comparing);
  };

  getWeekArray = (date) => {
    const start = date.clone().startOf("iMonth").startOf("week");
    const end = date.clone().endOf("iMonth").endOf("week");

    let count = 0;
    let current = start;
    const nestedWeeks = [];

    while (current.isBefore(end)) {
      const weekNumber = Math.floor(count / 7);
      nestedWeeks[weekNumber] = nestedWeeks[weekNumber] || [];
      nestedWeeks[weekNumber].push(current);
      current = current.clone().add(1, "day");
      count += 1;
    }

    return nestedWeeks;
  };

  getYearRange = (start, end) => {
    const startDate = this.moment(start).startOf("iYear");
    const endDate = this.moment(end).endOf("iYear");
    const years = [];

    let current = startDate;
    while (current.isBefore(endDate)) {
      years.push(current);
      current = current.clone().add(1, "iYear");
    }

    return years;
  };

  isWithinRange = (date, [start, end]) => date.isBetween(start, end, null, "[]");
}

export default AdapterMomentHijri;
